import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

import type { TicketTemplate } from '../../../types';
import type { ChangeWorkGroup } from '../../../types/changes';
import { listTicketTemplates } from '../../../services/ticketTemplates';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  applyTicketTemplateToIncidentDraft,
  type ApplyTicketTemplateResult,
} from '../ticketTemplateIncidentDraft';
import type { IncidentSendDraft, IncidentWorkflowOption } from '../incidentQuickSend';

interface Props {
  group: ChangeWorkGroup;
  workflows: IncidentWorkflowOption[];
  onApply: (patch: Partial<IncidentSendDraft>, notice?: string) => void;
  onError: (message: string | null) => void;
}

const IncidentTicketTemplatePicker: React.FC<Props> = ({ group, workflows, onApply, onError }) => {
  const [templates, setTemplates] = useState<TicketTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void listTicketTemplates()
      .then((items) => {
        if (!cancelled) setTemplates(items || []);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setTemplates([]);
          onErrorRef.current(
            getApiErrorMessage(err, 'Não foi possível carregar seus templates de chamado.'),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(
    () =>
      [...templates].sort((a, b) =>
        (a.title || '').localeCompare(b.title || '', 'pt-BR', { sensitivity: 'base' }),
      ),
    [templates],
  );

  const buildNotice = (result: ApplyTicketTemplateResult): string => {
    if (!result.skipped.length) {
      return 'Template aplicado aos campos do chamado.';
    }
    return `Template aplicado parcialmente (${result.skipped.join(', ')} ignorado(s) para esta área).`;
  };

  const handleApply = () => {
    const id = Number(selectedId);
    if (!id) {
      onError('Selecione um template de chamado.');
      return;
    }
    const row = templates.find((item) => item.id === id);
    if (!row) {
      onError('Template não encontrado.');
      return;
    }
    onError(null);
    const result = applyTicketTemplateToIncidentDraft(row, group, workflows);
    onApply(result.patch, buildNotice(result));
  };

  if (!loading && !options.length) {
    return (
      <p className="small text-muted mb-3">
        Você ainda não tem templates salvos no módulo de Incidentes (Novo Ticket → salvar template).
      </p>
    );
  }

  return (
    <Form.Group className="mb-3">
      <Form.Label className="mb-1">Template de chamado (opcional)</Form.Label>
      <div className="d-flex flex-wrap gap-2 align-items-start">
        <Form.Select
          aria-label="Template de chamado salvo"
          value={selectedId}
          disabled={loading}
          onChange={(event) => setSelectedId(event.target.value)}
          style={{ minWidth: '14rem', flex: '1 1 12rem' }}
        >
          <option value="">{loading ? 'Carregando templates…' : 'Selecione um template'}</option>
          {options.map((item) => (
            <option key={item.id} value={item.id}>
              {item.title}
              {item.category ? ` · ${item.category}` : ''}
            </option>
          ))}
        </Form.Select>
        <Button
          variant="outline-primary"
          size="sm"
          disabled={loading || !selectedId}
          onClick={handleApply}
        >
          Aplicar template
        </Button>
      </div>
      <Form.Text className="text-muted">
        Usa os mesmos templates individuais do Novo Ticket. O solicitante do chamado continua sendo o
        da mudança.
      </Form.Text>
    </Form.Group>
  );
};

export default IncidentTicketTemplatePicker;
